// Talmia 2.0 · Calls dashboard — AI-tagged call intelligence.
// Powered by `GET /api/calls` (filters), `/calls/stats` (KPI strip),
// `/calls/tag-batch` (background re-tag).
//
// Design: dark surface panels, brass metadata, orange totals, tone-coded chips.
// All chips + selects styled per Talmia 2.0 spec.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import LiveCount from "../../components/LiveCount";
import { toast } from "sonner";
import {
  PhoneCall, PhoneIncoming, PhoneOutgoing, Search, Sparkles, Filter, ChevronDown, AlertTriangle,
  RefreshCw, ArrowUpDown, ArrowDown, ArrowUp, Zap, Loader2, Clock, X, MessageSquareText, StickyNote, UserPlus, History, Star,
  CalendarDays, ExternalLink,
} from "lucide-react";
import { T2Panel, T2Chip } from "../quotes/_t2";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../../components/ui/hover-card";
import ConvertCallToLead from "./ConvertCallToLead";
import CallsFromNumberModal from "./CallsFromNumberModal";
import { useCallConfig, clsParam, CallClassFilters, ExportCsvButton, SettingsButton } from "./CallListExtras";
import CallClassificationSettings from "./CallClassificationSettings";
import Person360Overlay from "../../components/Person360Overlay";
import { useAuth } from "../../context/AuthContext";

const CATEGORY_OPTIONS = ["Quote", "Enquiry", "Complaint", "Supplier", "Delivery", "Order", "Support", "Sales", "Other"];
const SENTIMENT_OPTIONS = ["Positive", "Neutral", "Negative"];
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];
const DIRECTION_OPTIONS = ["Incoming", "Outgoing"];

const SENT_TONE = (v) => v === "Positive" ? "ok" : v === "Negative" ? "red" : "blue";
const PRIO_TONE = (v) => v === "High" ? "red" : v === "Medium" ? "warn" : "";
const DIR_TONE  = (v) => /out/i.test(String(v)) ? "orange" : "teal";
const DIR_ICON  = (v) => /out/i.test(String(v)) ? PhoneOutgoing : PhoneIncoming;
const RATING_TONE = (n) => n == null ? "var(--t2-grey4)" : n >= 75 ? "var(--t2-teal)" : n >= 50 ? "var(--t2-brass)" : "var(--t2-red)";

/* Date presets → {from,to} as local YYYY-MM-DD (backend compares ISO prefixes). */
const ymd = (d) => d.toLocaleDateString("en-CA");
const PERIODS = [
  { k: "all",   l: "All time" },
  { k: "today", l: "Today" },
  { k: "7d",    l: "7 days" },
  { k: "30d",   l: "30 days" },
  { k: "month", l: "This month" },
  { k: "custom", l: "Custom" },
];
function periodRange(period, cf, ct) {
  const now = new Date();
  const to = ymd(now);
  if (period === "today") return { from: to, to };
  if (period === "7d")   { const d = new Date(); d.setDate(d.getDate() - 6);  return { from: ymd(d), to }; }
  if (period === "30d")  { const d = new Date(); d.setDate(d.getDate() - 29); return { from: ymd(d), to }; }
  if (period === "month"){ const d = new Date(now.getFullYear(), now.getMonth(), 1); return { from: ymd(d), to }; }
  if (period === "custom") return { from: cf || "", to: ct || "" };
  return { from: "", to: "" };
}
const PRESET_SORTS = ["recent", "duration", "priority"];

/* 3-click sort cycle header: desc → asc → reset (server-side). */
function SortableTh({ label, colKey, sort, onSort, align = "left", style }) {
  const active = sort?.key === colKey;
  const dir = active ? sort.dir : null;
  const cycle = () => {
    if (!active) onSort({ key: colKey, dir: "desc" });
    else if (dir === "desc") onSort({ key: colKey, dir: "asc" });
    else onSort({ key: "recent", dir: "desc" });
  };
  return (
    <th onClick={cycle} data-testid={`sort-th-${colKey}`}
      title={active ? (dir === "desc" ? "Click for asc" : "Click to reset") : "Click to sort high → low"}
      style={{ textAlign: align, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...style }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: active ? "var(--t2-orange)" : undefined }}>
        {label}
        {active
          ? (dir === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)
          : <ArrowUpDown className="w-2.5 h-2.5" style={{ opacity: .35 }} />}
      </span>
    </th>
  );
}

const fmtDur = (secs) => {
  const n = Number(secs || 0);
  if (!n) return "—";
  const m = Math.floor(n / 60), s = n % 60;
  return `${m}m ${String(s).padStart(2, "0")}s`;
};

const dateShort = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    const today = new Date(); today.setHours(0,0,0,0);
    const day = new Date(d); day.setHours(0,0,0,0);
    const diff = Math.round((today - day) / 86400000);
    if (diff === 0) return `Today · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
    if (diff === 1) return `Yesterday · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
    return d.toLocaleDateString("en-GB");
  } catch { return String(v); }
};

/* Compact multi-select popover — Talmia 2.0 chip style */
function MultiSelect({ label, options, selected, onChange, facets, testid }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!e.target.closest(`[data-ms="${testid}"]`)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, testid]);

  const toggle = (v) => {
    const has = selected.includes(v);
    onChange(has ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  return (
    <div className="relative" data-ms={testid}>
      <button
        className="t2-msf-btn"
        onClick={() => setOpen((o) => !o)}
        data-testid={`filter-${testid}`}
      >
        <Filter className="w-3 h-3" />
        <span>{label}</span>
        {selected.length > 0 && (
          <span className="t2-mono" style={{
            marginLeft: 4, background: "var(--t2-orange-dim)",
            color: "var(--t2-orange)", padding: "1px 6px", borderRadius: 10, fontSize: 10, fontWeight: 700,
          }}>{selected.length}</span>
        )}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="t2-msf-pop" ref={(el) => {
          if (!el) return;
          const r = el.parentElement.getBoundingClientRect();
          const flip = r.left + 240 > window.innerWidth;
          el.style.left = flip ? "auto" : "0"; el.style.right = flip ? "0" : "auto";
        }}>
          {options.map((o) => {
            const on = selected.includes(o);
            const n = facets ? (facets[o] ?? 0) : null;
            return (
              <button
                key={o}
                onClick={() => toggle(o)}
                className={`t2-msf-row ${on ? "on" : ""}`}
                data-testid={`filter-${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="t2-msf-check" aria-hidden>{on ? "✓" : ""}</span>
                <span style={{ flex: 1, textAlign: "left" }}>{o}</span>
                <span className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 10 }}>{n}</span>
              </button>
            );
          })}
          {options.length === 0 && (
            <div className="t2-mono" style={{ padding: 10, color: "var(--t2-grey4)", fontSize: 11 }}>No options.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* KPI card in the top strip — optionally clickable to apply a quick filter */
function Kpi({ label, value, tone = "", icon: Icon, testid, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        background: active ? "var(--t2-surface3)" : "var(--t2-surface)",
        border: `1px solid ${active ? (tone ? `var(--t2-${tone})` : "var(--t2-orange)") : "var(--t2-border)"}`,
        borderRadius: 12,
        padding: "14px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        boxShadow: "0 8px 20px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.05)",
        minWidth: 130, textAlign: "left",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color .15s ease, transform .15s ease, background .15s ease",
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
      data-testid={testid}
    >
      <div style={{ minWidth: 0 }}>
        <div className="t2-mono" style={{
          fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".12em", color: "var(--t2-brass)", fontWeight: 700,
        }}>{label}</div>
        <div className="t2-mono" style={{
          fontSize: 26, fontWeight: 800, color: tone ? `var(--t2-${tone})` : "var(--t2-white)",
          fontVariantNumeric: "tabular-nums", marginTop: 4, lineHeight: 1,
        }}>{value}</div>
      </div>
      {Icon && (
        <div style={{
          width: 38, height: 38, borderRadius: 9,
          background: "var(--t2-surface3)",
          display: "grid", placeItems: "center",
          color: tone ? `var(--t2-${tone})` : "var(--t2-orange)",
          border: "1px solid var(--t2-border2)", flexShrink: 0,
        }}><Icon className="w-4 h-4" /></div>
      )}
    </button>
  );
}

export default function Calls() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [cats, setCats] = useState([]);
  const [sents, setSents] = useState([]);
  const [prios, setPrios] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dirs, setDirs] = useState([]);
  const [tagged, setTagged] = useState(""); // "" | "yes" | "no"
  const [match, setMatch] = useState("");   // "" | matched | unmatched | dismissed
  const [contactType, setContactType] = useState("");  // "" | customer | lead
  const [numberModal, setNumberModal] = useState(null);   // phone number for the by-number popup
  const [linkFor, setLinkFor] = useState(null);   // call id being linked
  const [linkQ, setLinkQ] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);
  const [convertFor, setConvertFor] = useState(null);   // call id → convert to lead
  const [sort, setSort] = useState({ key: "recent", dir: "desc" });
  const [period, setPeriod] = useState("all");          // all|today|7d|30d|month|custom
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [batching, setBatching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { cfg: clsCfg, tags: tagList } = useCallConfig();
  const [clsSel, setClsSel] = useState({});
  const [tagSel, setTagSel] = useState([]);
  const [review, setReview] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [p360, setP360] = useState(null);
  const { user: me } = useAuth();
  const isAdmin = (me?.role || "").toLowerCase() === "admin" || me?.super_admin;
  const LIMIT = 50;
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const genRef = useRef(0);

  const buildParams = useCallback((page) => {
    const p = new URLSearchParams({ page, limit: LIMIT, sort: sort.key, sort_dir: sort.dir });
    if (search.trim()) p.set("search", search.trim());
    if (cats.length) p.set("category", cats.join(","));
    if (sents.length) p.set("sentiment", sents.join(","));
    if (prios.length) p.set("priority", prios.join(","));
    if (agents.length) p.set("agent", agents.join(","));
    if (dirs.length) p.set("direction", dirs.join(","));
    if (tagged) p.set("tagged", tagged);
    if (match) p.set("match", match);
    if (contactType) p.set("contact_type", contactType);
    const range = periodRange(period, customFrom, customTo);
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    const cp = clsParam(clsSel);
    if (cp) p.set("cls", cp);
    if (tagSel.length) p.set("tag", tagSel.join(","));
    if (review) p.set("needs_review", "yes");
    if (transcript) p.set("has_transcript", transcript);
    return p;
  }, [search, cats, sents, prios, agents, dirs, tagged, match, contactType, sort, period, customFrom, customTo, clsSel, tagSel, review, transcript]);

  const fetchAt = useCallback(async (page, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const gen = genRef.current;
    try {
      const { data } = await api.get(`/calls?${buildParams(page).toString()}`);
      if (gen !== genRef.current) return;
      const batch = data.items || [];
      const t = data.total || 0;
      setCalls((prev) => (append ? [...prev, ...batch] : batch));
      setTotal(t);
      setFacets(data.facets || {});
      pageRef.current = page;
      setHasMore(page * LIMIT < t);
    } catch {
      if (gen === genRef.current && !append) setCalls([]);
    } finally {
      if (gen === genRef.current) { loadingRef.current = false; setLoading(false); }
    }
  }, [buildParams]);

  const load = useCallback(() => {
    genRef.current += 1;
    pageRef.current = 1;
    loadingRef.current = false;
    setHasMore(true);
    fetchAt(1, false);
  }, [fetchAt]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) fetchAt(pageRef.current + 1, true);
  }, [hasMore, fetchAt]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/calls/stats");
      setStats(data);
    } catch { setStats(null); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const runBatch = async () => {
    setBatching(true);
    try {
      const { data } = await api.post("/calls/tag-batch", { scope: "untagged", max: 100 });
      toast.success(`Scheduled tagging for ${data.scheduled} call${data.scheduled === 1 ? "" : "s"}. Refresh in a bit.`);
      setTimeout(() => { load(); loadStats(); }, 5000);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Batch tagging failed";
      toast.error(String(detail));
    } finally { setBatching(false); }
  };

  const activeFilterCount = cats.length + sents.length + prios.length + agents.length + dirs.length + (search ? 1 : 0) + (tagged ? 1 : 0) + (match ? 1 : 0) + (contactType ? 1 : 0) + (period !== "all" ? 1 : 0)
    + Object.values(clsSel).reduce((a, v) => a + v.length, 0) + tagSel.length + (review ? 1 : 0) + (transcript ? 1 : 0);

  // Unmatched-queue person search (debounced) for the Link picker.
  useEffect(() => {
    if (linkFor === null) return;
    const q = linkQ.trim();
    if (q.length < 2) { setLinkResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/calls/person-search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setLinkResults(data.items || []);
      } catch { if (!cancelled) setLinkResults([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [linkQ, linkFor]);

  const doLink = async (callId, personId, label) => {
    setLinkBusy(true);
    try {
      await api.post(`/calls/${callId}/link`, { person_id: personId });
      toast.success(`Linked call to ${label}`);
      setLinkFor(null); setLinkQ(""); setLinkResults([]);
      load(); loadStats();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Link failed"));
    } finally { setLinkBusy(false); }
  };

  const doDismiss = async (callId) => {
    try {
      await api.post(`/calls/${callId}/dismiss`);
      toast.success("Dismissed — removed from the unmatched queue");
      load(); loadStats();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Dismiss failed"));
    }
  };

  const agentOptions = useMemo(() => Object.keys(facets?.agent || {}).sort(), [facets]);
  const dirOptions   = useMemo(() => {
    const keys = Object.keys(facets?.direction || {}).filter(Boolean);
    return keys.length ? keys : DIRECTION_OPTIONS;
  }, [facets]);

  // Server-side sort now drives ordering across the full dataset, so the
  // loaded slice is rendered as-is.
  const displayed = calls;

  return (
    <Layout>
      <div data-testid="calls-page">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
          <div>
            <div className="t2-page-eyebrow">
              <PhoneCall className="w-3 h-3" /> Call Intelligence
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t2-white)", margin: "4px 0 3px", letterSpacing: "-.01em", display: "flex", alignItems: "center", gap: 12 }}>
              Calls
              <LiveCount shown={calls.length} total={total} noun="call" testId="calls-live-count" />
            </h1>
            <p className="t2-mono" style={{ fontSize: 11.5, color: "var(--t2-grey3)" }}>
              <span style={{ color: "var(--t2-white)" }}>{calls.length}</span> of <span style={{ color: "var(--t2-white)" }}>{total}</span> calls
              {stats && (
                <>
                  {" · "}<span style={{ color: "var(--t2-teal)" }}>{stats.tagged}</span> tagged
                  {" · "}<span style={{ color: "var(--t2-brass)" }}>{stats.untagged}</span> pending
                </>
              )}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="t2-btn-ghost"
              onClick={() => { load(); loadStats(); }}
              disabled={loading}
              data-testid="calls-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              className="t2-btn-primary"
              onClick={runBatch}
              disabled={batching || (stats?.untagged ?? 0) === 0}
              data-testid="calls-batch-tag"
              title={(stats?.untagged ?? 0) === 0 ? "Everything's tagged" : "Send untagged calls to the AI tagger"}
            >
              {batching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Analyse untagged ({stats?.untagged ?? "…"})
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 18 }}>
          <Kpi label="Total calls"    value={stats?.total ?? "…"}          icon={PhoneCall}     testid="kpi-total"
               active={!tagged && !sents.length} onClick={() => { setTagged(""); setSents([]); }} />
          <Kpi label="AI-tagged"      value={stats?.tagged ?? "…"}         icon={Sparkles}  tone="teal"
               testid="kpi-tagged"  active={tagged === "yes"} onClick={() => setTagged(tagged === "yes" ? "" : "yes")} />
          <Kpi label="Untagged"       value={stats?.untagged ?? "…"}       icon={Clock}     tone="warn"
               testid="kpi-untagged" active={tagged === "no"}  onClick={() => setTagged(tagged === "no" ? "" : "no")} />
          <Kpi label="Tagged · 24h"   value={stats?.tagged_24h ?? "…"}     icon={Zap}       tone="orange"
               testid="kpi-tagged-24h" active={period === "today" && tagged === "yes"}
               onClick={() => { setPeriod("today"); setTagged("yes"); }} />
          <Kpi label="Negative · open" value={stats?.negative_open ?? "…"} icon={AlertTriangle} tone="red"
               testid="kpi-negative" active={sents.length === 1 && sents[0] === "Negative"}
               onClick={() => setSents(sents.includes("Negative") ? [] : ["Negative"])} />
        </div>

        {/* Filter bar */}
        <div
          data-testid="calls-filters"
          style={{
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8,
            padding: 12, marginBottom: 12,
            background: "var(--t2-surface)",
            border: "1px solid var(--t2-border)",
            borderRadius: 10,
            boxShadow: "0 6px 18px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.04)",
          }}
        >
          <div style={{ position: "relative", flex: 1, minWidth: 240, maxWidth: 380 }}>
            <Search className="w-3.5 h-3.5" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--t2-grey3)" }} />
            <input
              className="t2-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search transcript, notes, contact, topic…"
              style={{ paddingLeft: 32 }}
              data-testid="calls-search"
            />
          </div>
          {/* Time range — fetches across the FULL dataset for the period */}
          <div style={{ display: "inline-flex", alignItems: "center", border: "1px solid var(--t2-border2)", borderRadius: 8, overflow: "hidden" }} data-testid="calls-period-filter">
            <CalendarDays className="w-3.5 h-3.5" style={{ margin: "0 7px", color: "var(--t2-brass)" }} />
            {PERIODS.map(({ k, l }) => (
              <button
                key={k}
                onClick={() => setPeriod(k)}
                className="t2-mono"
                style={{
                  padding: "7px 10px", fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em",
                  background: period === k ? "var(--t2-orange-dim)" : "transparent",
                  color: period === k ? "var(--t2-orange)" : "var(--t2-grey2)",
                  border: "none", cursor: "pointer", whiteSpace: "nowrap", fontWeight: period === k ? 800 : 600,
                }}
                data-testid={`calls-period-${k}`}
              >
                {l}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="calls-custom-range">
              <input type="date" className="t2-input" value={customFrom} max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)} style={{ padding: "6px 8px", fontSize: 11 }} data-testid="calls-custom-from" />
              <span className="t2-mono" style={{ color: "var(--t2-grey3)", fontSize: 11 }}>→</span>
              <input type="date" className="t2-input" value={customTo} min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)} style={{ padding: "6px 8px", fontSize: 11 }} data-testid="calls-custom-to" />
            </div>
          )}
          <MultiSelect label="AI category" options={CATEGORY_OPTIONS}  selected={cats}   onChange={setCats}   facets={facets.category}  testid="category" />
          <MultiSelect label="AI sentiment" options={SENTIMENT_OPTIONS} selected={sents}  onChange={setSents}  facets={facets.sentiment} testid="sentiment" />
          <MultiSelect label="AI priority" options={PRIORITY_OPTIONS}  selected={prios}  onChange={setPrios}  facets={facets.priority}  testid="priority" />
          <MultiSelect label="Direction" options={dirOptions}        selected={dirs}   onChange={setDirs}   facets={facets.direction} testid="direction" />
          <MultiSelect label="Agent"     options={agentOptions}      selected={agents} onChange={setAgents} facets={facets.agent}     testid="agent" />
          <CallClassFilters MultiSelect={MultiSelect} cfg={clsCfg} tagList={tagList} clsSel={clsSel} setClsSel={setClsSel}
            tagSel={tagSel} setTagSel={setTagSel} review={review} setReview={setReview} transcript={transcript} setTranscript={setTranscript} />
          <ExportCsvButton buildParams={buildParams} />
          {isAdmin && <SettingsButton onClick={() => setSettingsOpen(true)} />}
          <div style={{ display: "inline-flex", border: "1px solid var(--t2-border2)", borderRadius: 8, overflow: "hidden" }}>
            {[
              { k: "",    l: "All"      },
              { k: "yes", l: "Tagged"   },
              { k: "no",  l: "Untagged" },
            ].map(({ k, l }) => (
              <button
                key={k || "all"}
                onClick={() => setTagged(k)}
                className="t2-mono"
                style={{
                  padding: "7px 11px", fontSize: 10, textTransform: "uppercase", letterSpacing: ".1em",
                  background: tagged === k ? "var(--t2-orange-dim)" : "var(--t2-surface)",
                  color: tagged === k ? "var(--t2-orange)" : "var(--t2-grey2)",
                  border: "none", cursor: "pointer",
                }}
                data-testid={`calls-tagged-${k || "all"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div style={{ display: "inline-flex", border: "1px solid var(--t2-border2)", borderRadius: 8, overflow: "hidden" }} data-testid="calls-match-filter">
            {[
              { k: "",           l: "All" },
              { k: "unmatched",  l: `Unmatched${facets?.match?.unmatched != null ? ` (${facets.match.unmatched})` : ""}` },
              { k: "matched",    l: "Matched" },
              { k: "dismissed",  l: "Dismissed" },
            ].map(({ k, l }) => (
              <button
                key={k || "all-match"}
                onClick={() => setMatch(k)}
                className="t2-mono"
                style={{
                  padding: "7px 11px", fontSize: 10, textTransform: "uppercase", letterSpacing: ".1em",
                  background: match === k ? (k === "unmatched" ? "var(--t2-red-dim, rgba(239,68,68,.15))" : "var(--t2-orange-dim)") : "var(--t2-surface)",
                  color: match === k ? (k === "unmatched" ? "var(--t2-red)" : "var(--t2-orange)") : "var(--t2-grey2)",
                  border: "none", cursor: "pointer", whiteSpace: "nowrap",
                }}
                data-testid={`calls-match-${k || "all"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div style={{ display: "inline-flex", border: "1px solid var(--t2-border2)", borderRadius: 8, overflow: "hidden" }} data-testid="calls-contact-type-filter">
            {[
              { k: "",         l: "All" },
              { k: "customer", l: `Customers${facets?.contact_type?.customer != null ? ` (${facets.contact_type.customer})` : ""}` },
              { k: "lead",     l: `Leads${facets?.contact_type?.lead != null ? ` (${facets.contact_type.lead})` : ""}` },
            ].map(({ k, l }) => (
              <button
                key={k || "all-type"}
                onClick={() => setContactType(k)}
                className="t2-mono"
                style={{
                  padding: "7px 11px", fontSize: 10, textTransform: "uppercase", letterSpacing: ".1em",
                  background: contactType === k ? (k === "customer" ? "var(--t2-teal-dim, rgba(45,212,191,.15))" : "var(--t2-orange-dim)") : "var(--t2-surface)",
                  color: contactType === k ? (k === "customer" ? "var(--t2-teal)" : "var(--t2-orange)") : "var(--t2-grey2)",
                  border: "none", cursor: "pointer", whiteSpace: "nowrap",
                }}
                data-testid={`calls-type-${k || "all"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <ArrowUpDown className="w-3.5 h-3.5" style={{ color: "var(--t2-grey3)" }} />
            <select className="t2-input"
              value={PRESET_SORTS.includes(sort.key) ? sort.key : "__col"}
              onChange={(e) => { if (e.target.value !== "__col") setSort({ key: e.target.value, dir: "desc" }); }}
              title="Sort calls across the full dataset"
              style={{ paddingRight: 24 }} data-testid="calls-sort">
              {!PRESET_SORTS.includes(sort.key) && (
                <option value="__col">Column: {sort.key} ({sort.dir})</option>
              )}
              <option value="recent">Most recent</option>
              <option value="duration">Longest first</option>
              <option value="priority">Priority</option>
            </select>
            {(sort.key !== "recent" || sort.dir !== "desc") && (
              <button className="t2-mono" onClick={() => setSort({ key: "recent", dir: "desc" })}
                style={{ background: "none", border: "none", color: "var(--t2-orange)", cursor: "pointer", fontSize: 11 }}
                data-testid="calls-reset-col-sort">
                Reset sort
              </button>
            )}
            {activeFilterCount > 0 && (
              <button className="t2-mono"
                onClick={() => { setSearch(""); setCats([]); setSents([]); setPrios([]); setAgents([]); setDirs([]); setTagged(""); setMatch(""); setContactType(""); setPeriod("all"); setCustomFrom(""); setCustomTo(""); setClsSel({}); setTagSel([]); setReview(false); setTranscript(""); }}
                style={{ background: "none", border: "none", color: "var(--t2-red)", cursor: "pointer", fontSize: 11 }}
                data-testid="calls-clear-filters"
              >
                Clear ({activeFilterCount})
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="t2-tbl-wrap">
          <table className="t2-tbl" style={{ minWidth: 1500 }} data-testid="calls-table">
            <thead>
              <tr>
                <SortableTh label="When"      colKey="when"      sort={sort} onSort={setSort} style={{ width: 140 }} />
                <SortableTh label="Dir"       colKey="direction" sort={sort} onSort={setSort} style={{ width: 50 }} align="center" />
                <SortableTh label="Agent"     colKey="agent"     sort={sort} onSort={setSort} style={{ width: 140 }} />
                <SortableTh label="Contact"   colKey="contact"   sort={sort} onSort={setSort} style={{ width: 160 }} />
                <SortableTh label="Duration"  colKey="duration"  sort={sort} onSort={setSort} style={{ width: 90 }} align="right" />
                <SortableTh label="Category"  colKey="category"  sort={sort} onSort={setSort} style={{ width: 110 }} />
                <SortableTh label="Sentiment" colKey="sentiment" sort={sort} onSort={setSort} style={{ width: 110 }} />
                <SortableTh label="Priority"  colKey="priority"  sort={sort} onSort={setSort} style={{ width: 90 }} />
                <SortableTh label="Notes"     colKey="notes"     sort={sort} onSort={setSort} style={{ width: 50 }} align="center" />
                <th style={{ width: 210 }}>Match</th>
                <th style={{ width: 70 }} title="Other calls from this number">History</th>
                <th>AI summary + topics</th>
              </tr>
            </thead>
            <tbody>
              {loading && displayed.length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: "center", padding: 40, color: "var(--t2-grey3)" }}>
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading calls…
                </td></tr>
              )}
              {!loading && displayed.length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: "center", padding: 40, color: "var(--t2-grey3)" }}>
                  No calls match the current filters.
                </td></tr>
              )}
              {displayed.map((c) => {
                const jc = ((c.JCCall || {}).data) || {};
                const jcData = Array.isArray(jc) ? (jc[0] || {}) : jc;
                const dur = (jcData.call_duration || {}).total_duration;
                const ai = c.AITags || null;
                const contact = jcData.contact_name || jcData.contact_number || "—";
                const dirVal = (jcData.call_info || {}).direction || jcData.direction || "";
                const DirIcon = DIR_ICON(dirVal);
                const hasNotes = c.Notes && String(c.Notes).trim().length > 0;
                return (
                  <tr key={c._id}
                    onClick={() => navigate(`/lab/calls/${c._id}`)}
                    style={{ cursor: "pointer" }}
                    data-testid={`calls-row-${c._id}`}
                  >
                    <td className="t2-mono" style={{ color: "var(--t2-grey2)", fontSize: 11 }}>{dateShort(c.CreatedDate)}</td>
                    <td style={{ textAlign: "center" }}>
                      {dirVal ? (
                        <T2Chip tone={DIR_TONE(dirVal)}>
                          <DirIcon className="w-3 h-3 inline-block" style={{ marginRight: 3 }} />
                          {/out/i.test(dirVal) ? "Out" : "In"}
                        </T2Chip>
                      ) : <span className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 10 }}>—</span>}
                    </td>
                    <td>
                      <div style={{ color: "var(--t2-brass)", fontWeight: 700 }}>{jcData.agent_name || "—"}</div>
                      <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)" }}>{jcData.justcall_line_name || ""}</div>
                    </td>
                    <td onClick={(e) => { if (c.match?.name) e.stopPropagation(); }}>
                      {c.match?.name ? (<>
                        <button
                          data-testid={`call-contact-link-${c._id}`}
                          onClick={() => navigate(`/crm/persons/${c.match.person_id}`)}
                          title={`Open ${c.match.name}`}
                          style={{ textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer", maxWidth: 150 }}
                        >
                          <div style={{ color: "var(--t2-teal)", fontWeight: 700, display: "flex", alignItems: "center", gap: 4, lineHeight: 1.2 }}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.match.name}</span>
                            <ExternalLink className="w-3 h-3" style={{ opacity: .55, flexShrink: 0 }} />
                          </div>
                          <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.match.company || jcData.contact_number || ""}
                          </div>
                        </button>
                        <button type="button" onClick={() => setP360(c.match.person_id)} className="cc-360-btn" data-testid={`call-360-${c._id}`} title="Open Customer 360">360</button>
                      </>) : (
                        <>
                          <div style={{ color: "var(--t2-white)", fontWeight: 600 }}>{contact}</div>
                          <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)" }}>{jcData.contact_number || ""}</div>
                        </>
                      )}
                    </td>
                    <td className="t2-mono" style={{ textAlign: "right", color: "var(--t2-grey1)", fontVariantNumeric: "tabular-nums" }}>{fmtDur(dur)}</td>
                    <td>{ai?.category
                      ? <T2Chip tone="violet">{ai.category}</T2Chip>
                      : <span className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 10 }}>—</span>}
                    </td>
                    <td>{ai?.sentiment
                      ? <T2Chip tone={SENT_TONE(ai.sentiment)}>{ai.sentiment}</T2Chip>
                      : <span className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 10 }}>—</span>}
                    </td>
                    <td>{ai?.priority
                      ? <T2Chip tone={PRIO_TONE(ai.priority)}>{ai.priority}</T2Chip>
                      : <span className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 10 }}>—</span>}
                    </td>
                    <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      {hasNotes ? (
                        <HoverCard openDelay={120} closeDelay={80}>
                          <HoverCardTrigger asChild>
                            <button
                              data-testid={`notes-hover-${c._id}`}
                              style={{
                                background: "var(--t2-orange-dim)",
                                border: "1px solid var(--t2-orange-glow)",
                                borderRadius: 6, padding: "3px 6px",
                                color: "var(--t2-orange)", cursor: "help",
                                display: "inline-flex", alignItems: "center", gap: 3,
                              }}
                              onClick={() => navigate(`/lab/calls/${c._id}`)}
                            >
                              <StickyNote className="w-3 h-3" />
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent
                            align="start"
                            className="border-0 p-0"
                            style={{
                              background: "var(--t2-surface2)",
                              border: "1px solid var(--t2-border)",
                              borderRadius: 10, padding: 12, width: 320,
                              boxShadow: "0 10px 30px rgba(0,0,0,.45)",
                            }}
                            data-testid={`notes-tooltip-${c._id}`}
                          >
                            <div className="t2-mono" style={{
                              fontSize: 9.5, letterSpacing: ".14em", color: "var(--t2-brass)",
                              textTransform: "uppercase", fontWeight: 700, marginBottom: 6,
                              display: "flex", alignItems: "center", gap: 5,
                            }}>
                              <StickyNote className="w-3 h-3" /> Staff notes
                            </div>
                            <div style={{
                              fontSize: 12.5, color: "var(--t2-grey1)", lineHeight: 1.45,
                              whiteSpace: "pre-wrap", maxHeight: 240, overflowY: "auto",
                            }} className="t2-scroll">
                              {c.Notes}
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      ) : (
                        <span className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 10 }}>—</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} data-testid={`calls-match-cell-${c._id}`}>
                      {c.MatchStatus === "matched" ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <T2Chip tone={c.match?.type === "lead" ? "orange" : "teal"}>
                              {c.match?.type === "lead" ? "Lead" : "Customer"}
                            </T2Chip>
                            {c.match?.tier && <T2Chip tone="brass">{c.match.tier}</T2Chip>}
                          </div>
                          {c.match?.name ? (
                            <button
                              data-testid={`call-matched-name-${c._id}`}
                              onClick={() => navigate(`/crm/persons/${c.match.person_id}`)}
                              title="Open customer profile"
                              style={{ textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                              <div style={{ color: "var(--t2-white)", fontWeight: 700, fontSize: 12.5, lineHeight: 1.2 }}>{c.match.name}</div>
                              {c.match.company && (
                                <div className="t2-mono" style={{ color: "var(--t2-grey3)", fontSize: 10 }}>{c.match.company}</div>
                              )}
                            </button>
                          ) : (
                            <T2Chip tone="teal">Matched</T2Chip>
                          )}
                        </div>
                      ) : c.MatchStatus === "dismissed" ? (
                        <T2Chip tone="">Dismissed</T2Chip>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <T2Chip tone="red">Unmatched</T2Chip>
                          <button className="t2-mono" data-testid={`call-convert-lead-${c._id}`}
                            onClick={() => setConvertFor(c._id)}
                            title="Create a lead from this call"
                            style={{ background: "var(--t2-teal, #2dd4bf)", border: "1px solid var(--t2-teal, #2dd4bf)", color: "#06251f", fontWeight: 800, borderRadius: 6, padding: "3px 8px", fontSize: 10, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <UserPlus className="w-3 h-3" /> Lead
                          </button>
                          <button className="t2-mono" data-testid={`call-link-${c._id}`}
                            onClick={() => { setLinkFor(c._id); setLinkQ(""); setLinkResults([]); }}
                            style={{ background: "var(--t2-orange-dim)", border: "1px solid var(--t2-orange-glow)", color: "var(--t2-orange)", borderRadius: 6, padding: "3px 7px", fontSize: 10, cursor: "pointer" }}>
                            Link
                          </button>
                          <button className="t2-mono" data-testid={`call-dismiss-${c._id}`}
                            onClick={() => doDismiss(c._id)}
                            style={{ background: "none", border: "1px solid var(--t2-border2)", color: "var(--t2-grey2)", borderRadius: 6, padding: "3px 7px", fontSize: 10, cursor: "pointer" }}>
                            Dismiss
                          </button>
                        </div>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }} data-testid={`call-history-cell-${c._id}`}>
                      {jcData.contact_number ? (
                        <button
                          data-testid={`call-history-btn-${c._id}`}
                          onClick={() => setNumberModal(jcData.contact_number)}
                          title={`View all calls from ${jcData.contact_number}`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: (c.number_call_count > 1) ? "var(--t2-orange-dim)" : "var(--t2-surface3)",
                            border: `1px solid ${(c.number_call_count > 1) ? "var(--t2-orange-glow)" : "var(--t2-border2)"}`,
                            color: (c.number_call_count > 1) ? "var(--t2-orange)" : "var(--t2-grey2)",
                            borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                          }}>
                          <History className="w-3 h-3" /> {c.number_call_count ?? 1}
                        </button>
                      ) : (
                        <span className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 10 }}>—</span>
                      )}
                    </td>
                    <td>
                      {ai?.score?.overall != null && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 5,
                          background: "var(--t2-surface3)", border: "1px solid var(--t2-border2)", borderRadius: 6, padding: "2px 7px" }}
                          data-testid={`call-rating-${c._id}`}
                          title={`Call rating ${ai.score.overall}/100 · ${ai.score.band || ""}`}>
                          <Star className="w-3 h-3" style={{ color: RATING_TONE(ai.score.overall) }} />
                          <span className="t2-mono" style={{ fontSize: 11, fontWeight: 800, color: RATING_TONE(ai.score.overall) }}>{ai.score.overall}</span>
                          {ai.score.band && <span className="t2-mono" style={{ fontSize: 9.5, color: "var(--t2-grey3)" }}>{ai.score.band}</span>}
                        </div>
                      )}
                      {ai?.summary && (
                        <div style={{ color: "var(--t2-grey1)", fontSize: 12, marginBottom: 4, lineHeight: 1.35 }}>
                          {ai.summary}
                        </div>
                      )}
                      {!ai && hasNotes && (
                        <div className="t2-mono" style={{ color: "var(--t2-grey3)", fontSize: 11, fontStyle: "italic" }}>
                          {String(c.Notes).slice(0, 100)}{c.Notes.length > 100 ? "…" : ""}
                        </div>
                      )}
                      {ai?.topics && ai.topics.length > 0 && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                          {ai.topics.slice(0, 5).map((t) => (
                            <T2Chip key={t} tone="brass">{t}</T2Chip>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {calls.length > 0 && (
            <InfiniteSentinel
              onReach={loadMore}
              hasMore={hasMore}
              loading={loading}
              count={calls.length}
              total={total}
              testId="calls-sentinel"
            />
          )}
        </div>
      </div>

      {linkFor && (
        <div onClick={() => setLinkFor(null)} data-testid="calls-link-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "grid", placeItems: "center", zIndex: 60 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: 460, maxWidth: "92vw", background: "var(--t2-surface2)", border: "1px solid var(--t2-border)", borderRadius: 12, padding: 18, boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div className="t2-mono" style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--t2-brass)", fontWeight: 700 }}>
                Link call to customer
              </div>
              <button onClick={() => setLinkFor(null)} data-testid="calls-link-close"
                style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer" }}><X className="w-4 h-4" /></button>
            </div>
            <div style={{ position: "relative", marginBottom: 10 }}>
              <Search className="w-3.5 h-3.5" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--t2-grey3)" }} />
              <input autoFocus className="t2-input" value={linkQ} onChange={(e) => setLinkQ(e.target.value)}
                placeholder="Search name, email or phone…" style={{ paddingLeft: 32, width: "100%" }} data-testid="calls-link-search" />
            </div>
            <div className="t2-scroll" style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {linkQ.trim().length < 2 && (
                <div className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 11, padding: 8 }}>Type at least 2 characters…</div>
              )}
              {linkQ.trim().length >= 2 && linkResults.length === 0 && (
                <div className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 11, padding: 8 }}>No customers found.</div>
              )}
              {linkResults.map((r) => (
                <button key={r.id} disabled={linkBusy} onClick={() => doLink(linkFor, r.id, r.label)}
                  data-testid={`calls-link-result-${r.id}`}
                  style={{ textAlign: "left", background: "var(--t2-surface)", border: "1px solid var(--t2-border2)", borderRadius: 8, padding: "8px 10px", cursor: "pointer", color: "var(--t2-white)" }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{r.label}</div>
                  <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey3)" }}>{r.sublabel}{r.meta ? ` · ${r.meta}` : ""}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {convertFor && (
        <ConvertCallToLead
          callId={convertFor}
          onClose={() => setConvertFor(null)}
          onSaved={() => { load(); loadStats(); }}
        />
      )}

      <CallClassificationSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={() => window.location.reload()} />
      {p360 && <Person360Overlay personId={p360} onClose={() => setP360(null)} />}
      {numberModal && (
        <CallsFromNumberModal number={numberModal} onClose={() => setNumberModal(null)} />
      )}
    </Layout>
  );
}
